// Carry Roll StrikePanel — strike-quality & handicap dispersion (9.1, 9.2).

import { useState } from 'react';
import { RangeSlider } from 'calc-controls';
import { useStore } from '../../store';
import { useResult } from '../../store/selectors';
import { strikeModifier, handicapDispersion } from '@carry-roll/shared-logic';
import { computeSpinLoft } from '@carry-roll/shared-logic';
import { convertDistance } from '@carry-roll/shared-logic';
import { HelpTooltip } from '../calculator/HelpTooltip';

export function StrikePanel() {
  const result = useResult();
  const delivery = useStore((s) => s.request.delivery);
  const distanceUnit = useStore((s) => s.distanceUnit);
  const [strikePct, setStrikePct] = useState(90);
  const [handicap, setHandicap] = useState(10);

  const conv = (m: number) => Math.round(convertDistance(m, distanceUnit));
  const sm = strikeModifier(result, strikePct / 100);
  const disp = handicapDispersion(handicap);
  const spinLoft = computeSpinLoft(delivery);

  return (
    <div data-testid="strike-panel">
      <h3 className="subsection-title">
        Strike &amp; dispersion
      </h3>
      <div className="flex items-center gap-1">
        <span className="field-label">Strike quality</span>
        <HelpTooltip label="Strike quality">Models the effect of contact. 100% = centre strike; lower values shorten carry as for a mishit.</HelpTooltip>
      </div>
      <RangeSlider
        label="Strike quality"
        value={strikePct}
        onChange={setStrikePct}
        unit="%"
        min={0}
        max={100}
        step={5}
        decimals={0}
      />
      <p className="text-sm">
        Strike quality {strikePct}%: carry {conv(result.carryM)} → {conv(sm.adjustedCarryM)} {distanceUnit}.
      </p>
      <div className="mt-3">
        <div className="flex items-center gap-1">
          <span className="field-label">Handicap</span>
          <HelpTooltip label="Handicap">Scales expected lateral dispersion. Scratch ~8 m; each handicap stroke adds ~0.8 m.</HelpTooltip>
        </div>
        <RangeSlider
          label="Handicap"
          value={handicap}
          onChange={setHandicap}
          min={0}
          max={36}
          step={1}
          decimals={0}
        />
      </div>
      <p className="text-sm">
        Expected lateral dispersion: {conv(disp)} {distanceUnit}
      </p>
      {spinLoft && (
        <p
          className="text-sm mt-2"
          data-testid="spin-loft"
          style={{ color: spinLoft.outOfRange ? 'var(--color-warn)' : 'var(--color-text-muted)' }}
        >
          {spinLoft.message}
        </p>
      )}
      <button type="button" className="btn mt-2" onClick={() => useStore.getState().resetInputs()}>
        Reset shot
      </button>
    </div>
  );
}
